/**
 * WONE-612: shared OTP code-entry screen.
 *
 * One implementation, two routes:
 *
 *   - /auth/otp/signin  → purpose="login",  uses verifyLoginOtp
 *   - /auth/otp/signup  → purpose="signup", uses verifySignupOtp
 *
 * On mount, calls ``requestEmailOtp(purpose, email)`` to seed the
 * server-driven resend cooldown. The server caches first-seen
 * (purpose, email) so re-entering the screen mid-window returns the
 * *remaining* seconds — no client-side fallback constant.
 *
 * Mobile parity: src/app/(auth)/signin/email-otp/code.tsx +
 *                src/app/(auth)/signup/email-otp/code.tsx (PRs #816/#820).
 */

import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "lib/i18n/use-translation";

import {
  AuthLayout,
  Heading,
  OtpCodeField,
  OtpCodeFieldHandle,
  ResendRow,
} from "apps/user/common/components/auth-layout";
import { useAuth } from "providers/auth";
import {
  OtpError,
  OtpPurpose,
  requestEmailOtp,
  verifyLoginOtp,
  verifySignupOtp,
} from "providers/auth-tokens";
import { logError } from "utility/Function";
import { setLastUsedEmail } from "utility/last-used-email";
import { trackEvent } from "utility/mixpanel";

import styles from "./otp-code-screen.module.scss";

export interface OtpCodeScreenProps {
  purpose: OtpPurpose;
}

// Error copy stays inline (not in the translation catalog): these are the
// auth/permission-adjacent messages owned by the error-code contract work
// (WONE-1598) — localising them here would front-run that boundary. Titles
// and subtitles are UI chrome and come from the `auth` catalog via t().
const COPY = {
  login: {
    wrongCode: "That code didn't match. Please try again.",
    lockout: "Too many attempts. Tap Resend for a new code.",
    network: "Couldn't verify the code. Please try again.",
  },
  signup: {
    wrongCode: "That code didn't match. Please try again.",
    lockout: "Too many attempts. Tap Resend for a new code.",
    network: "Couldn't verify the code. Please try again.",
    exists: "An account already exists for this email. Sign in instead.",
  },
} as const;

const OtpCodeScreen: React.FC<OtpCodeScreenProps> = ({ purpose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("auth");
  const email = (
    new URLSearchParams(location.search).get("email") || ""
  ).toLowerCase();

  const fieldRef = useRef<OtpCodeFieldHandle>(null);
  const seededRef = useRef(false);
  const { loadCurrentSession } = useAuth();

  const [code, setCode] = useState("");
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  // ``null`` until the server has responded with the first cooldown
  // window. While null the ResendRow renders disabled + number-less;
  // the OTP field itself stays interactive (mobile parity PR #828 —
  // disabling the field would break iOS QuickType tap-to-fill).
  const [cooldown, setCooldown] = useState<number | null>(null);

  // Defensive: if the user deep-links here without an email, bounce
  // back to the unified entry screen. They have no context to resume.
  useEffect(() => {
    if (!email) navigate("/email-entry", { replace: true });
  }, [email, navigate]);

  // Seed the cooldown from the server exactly once per mount. Server
  // is the single source of truth — re-entering the screen mid-window
  // returns the *remaining* time, not a fresh window. On failure we
  // surface the "couldn't sync" error inline so the user knows the
  // resend timer hasn't started — mobile parity (PR #828).
  useEffect(() => {
    if (seededRef.current || !email) return;
    seededRef.current = true;
    requestEmailOtp(purpose, email)
      .then(({ cooldown_seconds }) => setCooldown(cooldown_seconds))
      .catch((err) => {
        logError("Initial OTP cooldown sync failed", err as Error);
        setInlineError("Couldn't sync with server. Please try again.");
      });
  }, [email, purpose]);

  const copy = COPY[purpose];

  const title =
    purpose === "login" ? t("otp.titleSignIn") : t("otp.titleSignUp");

  const screen = purpose === "login" ? "signin_otp" : "signup_otp";

  const handleVerify = async (codeToSubmit: string): Promise<void> => {
    if (submitting || !email) return;
    setSubmitting(true);
    setInlineError(null);
    trackEvent("Email OTP Verify Submitted", { Purpose: purpose });
    try {
      if (purpose === "login") {
        await verifyLoginOtp({ email, code: codeToSubmit });
      } else {
        await verifySignupOtp({ email, code: codeToSubmit });
      }
      // WONE-612 (PR #828 parity): cache the email that just succeeded
      // so the unified entry screen prefills it on a future visit.
      setLastUsedEmail(email);
      // The access token is in memory; populate the user profile so
      // the auth gate flips and the next render redirects us into
      // onboarding / home (see ``user/index.js``).
      await loadCurrentSession();
      // Honour the deep-link the user was trying to reach pre-sign-in
      // (threaded through navigate-to-method → router state). Mirrors
      // the SSO PKCE-state path; without this the user lands on / and
      // loses their original destination after OTP verify.
      const returnTo = (location.state as { returnTo?: string } | null)
        ?.returnTo;
      navigate(returnTo || "/");
    } catch (err) {
      if (err instanceof OtpError) {
        if (err.status === 429) {
          setInlineError(copy.lockout);
          fieldRef.current?.reset();
          setCode("");
          trackEvent("Email OTP Error", {
            Purpose: purpose,
            "Error Code": "rate_limited",
          });
          trackEvent("Auth Server Error Shown", {
            Screen: screen,
            "Error Code": "rate_limited",
          });
          return;
        }
        if (purpose === "signup" && err.status === 409) {
          setInlineError(
            (copy as typeof COPY.signup).exists
          );
          trackEvent("Email OTP Error", {
            Purpose: purpose,
            "Error Code": "account_exists",
          });
          trackEvent("Auth Server Error Shown", {
            Screen: screen,
            "Error Code": "account_exists",
          });
          return;
        }
        if (err.status === 401) {
          setInlineError(copy.wrongCode);
          fieldRef.current?.reset();
          setCode("");
          trackEvent("Email OTP Error", {
            Purpose: purpose,
            "Error Code": "wrong_code",
          });
          trackEvent("Auth Validation Error Shown", {
            Field: "otp",
            Screen: screen,
            "Error Code": "wrong_code",
          });
          return;
        }
      }
      logError("Verify OTP failed", err as Error);
      setInlineError(copy.network);
      trackEvent("Email OTP Error", {
        Purpose: purpose,
        "Error Code": "network",
      });
      trackEvent("Auth Server Error Shown", {
        Screen: screen,
        "Error Code": "network",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (): Promise<void> => {
    if (!email || resending) return;
    setResending(true);
    setInlineError(null);
    trackEvent("Email OTP Resend Tapped", { Purpose: purpose });
    try {
      const { cooldown_seconds } = await requestEmailOtp(purpose, email);
      setCooldown(cooldown_seconds);
      setCode("");
      fieldRef.current?.reset();
    } catch (err) {
      logError("Resend OTP failed", err as Error);
      setInlineError("Couldn't send a new code. Please try again.");
      trackEvent("Email OTP Error", {
        Purpose: purpose,
        "Error Code": "resend_failed",
      });
      trackEvent("Auth Server Error Shown", {
        Screen: screen,
        "Error Code": "resend_failed",
      });
    } finally {
      setResending(false);
    }
  };

  if (!email) return null;

  return (
    <AuthLayout variant="form" onBack={() => navigate(-1)}>
      <div className={styles["body"]}>
        <Heading>{title}</Heading>
        <p className={styles["email-line"]}>
          {t("otp.subtitle")} <strong>{email}</strong>
        </p>

        <OtpCodeField
          ref={fieldRef}
          trackingScreen={screen}
          value={code}
          onChange={(next) => {
            if (inlineError) setInlineError(null);
            setCode(next);
          }}
          onComplete={handleVerify}
          hasError={Boolean(inlineError)}
          // ``loading`` already implies ``disabled`` inside OtpCodeField
          // (see ``isInactive`` in otp-code-field.tsx) — passing both
          // was a redundancy flagged in the #1172 review.
          //
          // Mobile parity (PR #828): block input only while *verifying*,
          // never during the initial cooldown seed. Disabling the field
          // mid-seed breaks iOS Safari's ``autocomplete="one-time-code"``
          // tap-to-fill suggestion.
          loading={submitting}
          autoFocus
        />

        {inlineError && (
          <p className={styles["inline-error"]} role="alert">
            {inlineError}
          </p>
        )}

        <ResendRow
          cooldownSeconds={cooldown}
          resending={resending}
          onResend={handleResend}
        />

        {/* Quiet hint below the resend row. Mobile parity (PR #828) —
         * helps users who haven't seen the email yet understand the
         * expected wait + where it might be. */}
        <p className={styles["hint"]}>
          {t("otp.deliveryHintLine1")}
          <br />
          {t("otp.deliveryHintLine2")}
        </p>
      </div>
    </AuthLayout>
  );
};

export default OtpCodeScreen;
